import { useEffect } from 'react'
import {
  MemoryRouter,
  useLocation,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useTabsStore } from '@/stores/tabs.store'
import AppRoutes from './AppRoutes'

// Her sekmeyi dış router bağlamından ayırmak için boş context değerleri.
// Böylece sekme içindeki MemoryRouter "iç içe Router" hatası vermeden mount olur
// ve kendi bağımsız geçmişine sahip olur (geri/ileri sekmeye özel çalışır).
const EMPTY_LOCATION_CTX = null as never
const EMPTY_ROUTE_CTX = { outlet: null, matches: [], isDataRoute: false } as never

// Sekmenin iç (MemoryRouter) konumunu store'a yansıtır → sidebar highlight + drill-down.
function LocationReporter({ tabId }: { tabId: string }) {
  const loc = useLocation()
  const updateCurrentPath = useTabsStore((s) => s.updateCurrentPath)
  useEffect(() => {
    updateCurrentPath(tabId, loc.pathname)
  }, [tabId, loc.pathname, updateCurrentPath])
  return null
}

export default function Workspace() {
  const tabs = useTabsStore((s) => s.tabs)
  const activeId = useTabsStore((s) => s.activeId)
  const setActive = useTabsStore((s) => s.setActive)
  const closeTab = useTabsStore((s) => s.closeTab)
  const openTab = useTabsStore((s) => s.openTab)

  // İlk açılış → Ana Sayfa sekmesi
  useEffect(() => {
    if (tabs.length === 0) openTab('/dashboard', 'Ana Sayfa')
  }, [tabs.length, openTab])

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      {/* Sekme çubuğu */}
      <div className="flex items-stretch gap-1 px-2 pt-1.5 bg-bg-surface border-b border-bg-border overflow-x-auto shrink-0">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const active = tab.id === activeId
          return (
            <div
              key={tab.id}
              role="button"
              tabIndex={0}
              onClick={() => setActive(tab.id)}
              onAuxClick={(e) => { if (e.button === 1) closeTab(tab.id) }}
              className={cn(
                'group flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-t-md text-sm cursor-pointer select-none whitespace-nowrap border border-b-0 transition-colors',
                active
                  ? 'bg-bg-base border-bg-border text-text-primary font-medium'
                  : 'bg-transparent border-transparent text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
              )}
            >
              {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate max-w-[180px]">{tab.title}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); closeTab(tab.id) }}
                title="Sekmeyi kapat"
                className={cn(
                  'w-4 h-4 flex items-center justify-center rounded shrink-0 transition-colors',
                  'text-text-muted hover:text-text-primary hover:bg-bg-border',
                  active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )
        })}
      </div>

      {/* İçerik — tüm sekmeler mount kalır (state korunur), sadece aktif görünür.
          Her sekme dış router'dan ayrık kendi MemoryRouter'ında çalışır. */}
      <div className="flex-1 min-h-0 relative bg-bg-base">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="absolute inset-0 overflow-auto"
            style={{ display: tab.id === activeId ? 'block' : 'none' }}
          >
            <div className="p-4 sm:p-5 lg:p-6 min-h-full">
              <UNSAFE_RouteContext.Provider value={EMPTY_ROUTE_CTX}>
                <UNSAFE_LocationContext.Provider value={EMPTY_LOCATION_CTX}>
                  <MemoryRouter initialEntries={[tab.path]}>
                    <LocationReporter tabId={tab.id} />
                    <AppRoutes />
                  </MemoryRouter>
                </UNSAFE_LocationContext.Provider>
              </UNSAFE_RouteContext.Provider>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
